import { restoreResumeStructure } from './resumeStructure';

/**
 * Fix UTF-8 mojibake (e.g. UTF-8 interpreted as Latin-1/Windows-1252).
 * Run early so bullets, quotes, and dashes from open-source resume tools render correctly.
 */
export function fixUtf8Mojibake(text: string): string {
  if (!text || typeof text !== 'string') return text;
  return text
    // Bullet: common mojibake for U+2022 (•)
    .replace(/\u0393\u00C7\u00F3/g, '\u2022')
    .replace(/ΓÇó/g, '\u2022')
    .replace(/\u00E2\u0080\u0092/g, '\u2022')   // â€' (often mis-decoded bullet)
    .replace(/â€¢/g, '\u2022')
    .replace(/Ã¢â‚¬Â¢/g, '\u2022')
    .replace(/\u2013/g, '-')                     // en dash → hyphen when used as list
    .replace(/\u2014/g, '-')                    // em dash → hyphen
    // Curved/smart quotes and apostrophes
    .replace(/\u00E2\u0080\u0099/g, "'")
    .replace(/\u00E2\u0080\u009C/g, '"')
    .replace(/\u00E2\u0080\u009D/g, '"')
    .replace(/\u00E2\u0080\u0093/g, '-')
    .replace(/\u00E2\u0080\u0094/g, '-')
    .replace(/\u2018/g, "'")
    .replace(/\u2019/g, "'")
    .replace(/\u201C/g, '"')
    .replace(/\u201D/g, '"')
    // Ellipsis
    .replace(/\u00E2\u0080\u00A6/g, '...')
    .replace(/\u2026/g, '...')
    // Non-breaking hyphen → hyphen (e.g. in URLs)
    .replace(/\u2011/g, '\u002D');
}

/** Strip box/junk characters that often appear in PDFs (□ ☐ ■ etc.) — remove or replace with bullet at line start. */
export function stripJunkBoxesForDisplay(text: string): string {
  if (!text || typeof text !== 'string') return text;
  const BULLET = '\u2022'; // •
  return text
    .replace(/\u25A1/g, BULLET)    // □ WHITE SQUARE (common junk)
    .replace(/\u2610/g, BULLET)   // ☐ BALLOT BOX
    .replace(/\u25A0/g, BULLET)   // ■ BLACK SQUARE (when at line start)
    .replace(/\u2B1C/g, ' ')     // ⬜ WHITE LARGE SQUARE → space
    .replace(/\u2B1B/g, ' ')     // ⬛ BLACK LARGE SQUARE → space
    .replace(/\u2752/g, ' ')     // ❒ (other box)
    .replace(/\u25FB/g, ' ')     // ◻ WHITE MEDIUM SQUARE
    .replace(/\u25FC/g, ' ');    // ◼ BLACK MEDIUM SQUARE
}

/** Normalize all bullet-like and list symbols to a single readable bullet (•). Improves parsing and preview. */
function normalizeBulletsAndSymbols(text: string): string {
  if (!text || typeof text !== 'string') return text;
  const BULLET = '\u2022'; // •
  return text
    .replace(/\u25AA/g, BULLET)   // ▪
    .replace(/\u25A1/g, BULLET)   // □ (when used as list)
    .replace(/\u2023/g, BULLET)   // ‣
    .replace(/\u2043/g, BULLET)   // ⁃
    .replace(/\u2219/g, BULLET)   // ∙
    .replace(/\u25CF/g, BULLET)   // ●
    .replace(/\u25E6/g, BULLET)   // ◦ (white bullet)
    .replace(/\u25CB/g, BULLET)   // ○ (when at line start as list)
    .replace(/\u00B7/g, BULLET)   // · middle dot used as bullet
    .replace(/\u22C5/g, BULLET)   // ⋅
    .replace(/\u2024/g, BULLET)   // ․ (one dot leader)
    .replace(/\uFE52/g, BULLET)   // ﹒ small full stop
    .replace(/\uF0B7/g, BULLET)   //  Wingdings / Word bullet (common PDF extract)
    .replace(/\uF0A7/g, BULLET)   //  Wingdings square bullet
    // Moji bullet sequences that might remain after fixUtf8Mojibake
    .replace(/\u00E2\u0080\u00A2/g, BULLET)
    .replace(/â€¢/g, BULLET)
    .replace(/Ã¢â‚¬Â¢/g, BULLET)
    .replace(/âˆ'/g, BULLET)
    .replace(/Ã¢Ë†'/g, BULLET);
}

/** Strip zero-width, replacement char, and other invisible/blocking chars that break display. */
function stripInvisibleAndReplacementChars(text: string): string {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/\uFFFD/g, '')       // replacement char
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')  // zero-width space, joiners, BOM
    .replace(/\u00AD/g, '')       // soft hyphen
    .replace(/[\u034F\u061C]/g, '') // Arabic letter mark, combining grapheme joiner
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
}

/** Strip boxes, rectangles, lines: Unicode box-drawing and block elements → space. Leaves bullets (•) and text. */
function stripBoxesAndLines(text: string): string {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/[\u2500-\u257F]/g, ' ')
    .replace(/[\u2580-\u259F]/g, ' ')
    .replace(/[\u25A0-\u25FF]/g, (ch) => (ch === '\u2022' || ch === '\u25E6' ? ch : ' '));
}

/** True if the line is only decorative (dashes, underscores, equals, dots, boxes — no real words). */
function isDecorativeLine(line: string): boolean {
  const t = line.trim();
  if (t.length === 0) return true;
  if (t.length >= 2 && /^[\s\-_=*·•▪▬─═━＿|○●◦∙]+$/.test(t)) return true;
  if (t.length >= 2 && /^[.\s\-_=*]{2,}$/.test(t)) return true;
  if (/[\u2500-\u257F]/.test(t) && !/[a-zA-Z]{2,}/.test(t)) return true;
  if (t.length >= 3 && !/[a-zA-Z]/.test(t)) return true;
  return false;
}

/** Remove common header/footer and decorative noise: lines, boxes, rectangles, fancy borders. */
function removeNoise(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = stripBoxesAndLines(text);
  const lines = out.split(/\r\n|\r|\n/);
  const result: string[] = [];

  const noisePatterns = [
    /^\s*page\s+\d+\s*(of\s*\d+)?\s*$/i,
    /^\s*[-_*=·•▪○●◦∙]{2,}\s*$/,
    /^\s*(confidential|curriculum vitae)\s*$/i,
    /^\s*©\s*|^\s*copyright\s*©/i,
    /^\s*generated\s+by\s+.+$/i,
    /^\s*created\s+with\s+.+$/i,
    /^\s*http[s]?:\/\/\S+\s*$/,
    /^\s*last\s+updated\s*:?\s*\d/i,
    /^\s*printed\s+on\s*.+$/i,
    /^\s*lorem\s+ipsum/i,
    /^\s*\[[\s\w]+\]\s*$/,  // placeholder [ placeholder text ]
    /^[\s\uFFFD\u200B-\u200D\u2060\uFEFF]+$/,  // only invisible/replacement
  ];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      result.push('');
      continue;
    }
    if (isDecorativeLine(trimmed)) {
      result.push('');
      continue;
    }
    const isNoise = noisePatterns.some((p) => p.test(trimmed));
    if (isNoise) {
      result.push('');
      continue;
    }
    result.push(line);
  }
  return result.join('\n');
}

/**
 * Repair PDF extraction artifacts: spaces inserted in the middle of words.
 * - All-caps single-letter spacing: "P R O F E S S I O N A L" → "PROFESSIONAL"
 * - Single cap + space + rest of word: "P ROFESSIONAL" → "PROFESSIONAL", "P rivate" → "Private"
 * - Common broken words (progr ams → programs, eB anking → eBanking, etc.)
 */
export function repairPdfWordSpacing(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  // Collapse spaces in sequences of single capital letters (P R O F E S S I O N A L → PROFESSIONAL)
  out = out.replace(/((?:[A-Z]\s)+[A-Z])\b/g, (m) => m.replace(/\s/g, ''));
  // Single capital + space + (capital + lowercase): "P ROFESSIONAL" → "PROFESSIONAL"
  out = out.replace(/\b([A-Z])\s+([A-Z][a-z]+)\b/g, '$1$2');
  // Single capital + space + lowercase word (common PDF break): "P rivate" → "Private"
  out = out.replace(/\b([A-Z])\s+(rivate|rofessional|ersonal|hone|ail|obile)\b/gi, (_, c, rest) => c + rest.toLowerCase());
  // CamelCase broken: "eB anking" → "eBanking"
  out = out.replace(/\b(eB)\s+(anking)\b/gi, 'eBanking');
  out = out.replace(/\b(progr)\s+(ams)\b/gi, 'programs');
  out = out.replace(/\b(demo)\s+(nstration)\b/gi, 'demonstration');
  out = out.replace(/\b(awarene)\s+(ss)\b/gi, 'awareness');
  out = out.replace(/\b(bus)\s+(iness)\b/gi, 'business');
  out = out.replace(/\b(d)\s+(ashboard)\b/gi, 'dashboard');
  out = out.replace(/\bo\s+n\s+\b/g, 'on ');
  // E - Mail → E-Mail, M obile → Mobile
  out = out.replace(/\b(E)\s*-\s*(Mail)\b/gi, 'E-Mail');
  out = out.replace(/\b(M)\s+obile\b/gi, 'Mobile');
  out = out.replace(/\b(S)\s+ep\b/g, 'Sep');
  // PDF junk: underscore inside word (e.g. "Skille_d" → "Skilled", "busi_ness" → "business")
  out = out.replace(/\bSkille_d\b/gi, 'Skilled');
  out = out.replace(/\bbusi_ness\b/gi, 'business');
  out = out.replace(/\baware_ness\b/gi, 'awareness');
  // Email: remove space before @ (e.g. "roiiftd @gmail.com" → "roiiftd@gmail.com")
  out = out.replace(/\s+@/g, '@');
  return out;
}

/** Normalize whitespace: unicode spaces → space, collapse internal spaces. Control/invisible chars stripped earlier. */
function normalizeWhitespaceAndSpecialChars(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00A0/g, ' ')
    .replace(/\u2000|\u2001|\u2002|\u2003|\u2004|\u2005|\u2006|\u2007|\u2008|\u2009|\u200A|\u202F|\u205F|\u3000/g, ' ');
  const lines = out.split('\n');
  out = lines
    .map((line) => line.replace(/\t+/g, ' ').replace(/\s{2,}/g, ' ').trimEnd())
    .join('\n');
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Preprocessing: fix encoding, normalize bullets/symbols, strip junk, then normalize.
 * Result is clean text for parsing and preview — no symbols that block visualization.
 */
export function preprocessResumeText(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = fixUtf8Mojibake(text);
  out = stripInvisibleAndReplacementChars(out);
  out = repairPdfWordSpacing(out);
  out = normalizeBulletsAndSymbols(out);
  out = stripBoxesAndLines(out);
  out = removeNoise(out);
  out = normalizeWhitespaceAndSpecialChars(out);
  out = out.replace(/\n*(?:page\s+\d+\s+of\s+\d+|page\s+\d+)\n*/gi, '\n');
  out = out.replace(/\n{3,}/g, '\n\n');
  return out.trim();
}

/**
 * Clean section content for display and bucketing: fix encoding, normalize bullets, strip junk boxes, strip invisible.
 * No aggressive noise removal (section content is already segmented).
 */
export function preprocessSectionContent(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = fixUtf8Mojibake(text);
  out = stripInvisibleAndReplacementChars(out);
  out = stripJunkBoxesForDisplay(out);
  out = normalizeBulletsAndSymbols(out);
  out = normalizeWhitespaceAndSpecialChars(out);
  return out.trim();
}

/**
 * Light preprocessing for section preview: only strips invisible/replacement chars and normalizes line breaks.
 * Preserves markdown formatting (**, *, ~~, __) so user formatting reflects correctly in the preview.
 */
export function preprocessSectionContentForPreview(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = stripInvisibleAndReplacementChars(text);
  out = out.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return out.trim();
}

/**
 * Normalize resume text for consistent display and export (result view, TXT, PDF, DOCX).
 * - Fixes mojibake, normalizes bullets, restores structure (one line per bullet/section), normalizes line breaks.
 */
export function normalizeResumeText(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = fixUtf8Mojibake(text);
  out = stripInvisibleAndReplacementChars(out);
  out = normalizeBulletsAndSymbols(out);
  out = restoreResumeStructure(out);
  out = out.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = out.split('\n').map((line) => line.trimEnd());
  const result: string[] = [];
  let prevBlank = false;
  for (const line of lines) {
    const isBlank = line.trim().length === 0;
    if (isBlank && prevBlank) continue;
    result.push(line);
    prevBlank = isBlank;
  }
  return result.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Sanitize text for PDF generation (WinAnsi / Windows-1252 encoding).
 * Replaces Unicode characters that StandardFonts cannot encode with ASCII equivalents,
 * then strips any remaining character outside the WinAnsi code page.
 */
export function sanitizeTextForPdf(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = fixUtf8Mojibake(text)
    .replace(/\u2011/g, '\u002D')   // NON-BREAKING HYPHEN → HYPHEN-MINUS
    .replace(/\u2012/g, '\u002D')   // FIG DASH → HYPHEN-MINUS
    .replace(/\u2013/g, '\u002D')   // EN DASH → HYPHEN-MINUS
    .replace(/\u2014/g, '\u002D')   // EM DASH → HYPHEN-MINUS
    .replace(/\u2018/g, "'")        // LEFT SINGLE QUOTATION MARK
    .replace(/\u2019/g, "'")        // RIGHT SINGLE QUOTATION MARK
    .replace(/\u201C/g, '"')        // LEFT DOUBLE QUOTATION MARK
    .replace(/\u201D/g, '"')        // RIGHT DOUBLE QUOTATION MARK
    .replace(/\u2026/g, '...')      // HORIZONTAL ELLIPSIS
    .replace(/\u00A0/g, ' ')        // NO-BREAK SPACE → SPACE
    .replace(/\u2022/g, '*');       // BULLET → asterisk (WinAnsi-safe)
  // WinAnsi supports 0x20-0x7E (ASCII printable) and 0xA0-0xFF (Latin-1 supplement)
  out = out.replace(/[\s\S]/g, (ch) => {
    const code = ch.charCodeAt(0);
    if (code >= 0x20 && code <= 0x7E) return ch;  // ASCII printable
    if (code >= 0xA0 && code <= 0xFF) return ch;  // Latin-1 supplement
    return ' ';
  });
  return out;
}
